import { useEffect, useState } from 'react';

import { Box, Button } from '@mui/material';
import { Form, Formik } from 'formik';

import { useParams } from '@redwoodjs/router';
import { useQuery } from '@redwoodjs/web';

import HeadingLabeledValueCard from 'src/components/HeadingLabeledValueCard';
import Loader from 'src/components/Loader/Loader';
import AddServiceType from 'src/components/ServiceType';
import { showErrorToast } from 'src/components/Toasts/toast';
import { contractTerms } from 'src/constants/contractTerms';
import { addServiceRequirementsValidation } from 'src/constants/formValidations/addMerchantOutletStep';
import { useAddMerchantContext } from 'src/contexts/AddMerchantProvider';
import { ProductOrderProvider } from 'src/contexts/ProductOrderProvider';
import { ServiceOrderProvider } from 'src/contexts/ServiceOrderProvider';
import { TransactionChargeProvider } from 'src/contexts/TransactionChargeProvider';
import { GET_MERCHANT } from 'src/graphql/queries/merchants.query';
import { useAdditionalCharges } from 'src/hooks';
import { SelectedProducts } from 'src/hooks/usePricings';
import { useTransactionData } from 'src/hooks/useTransactionCharges';
import getPostalLabel from 'src/lib/postalLabel';
import ProductOrders from 'src/pages/AddMerchantPage/components/ProductOrders';
import { ServiceOrders } from 'src/pages/AddMerchantPage/components/ServiceOrders';
import ServiceTotals from 'src/pages/AddMerchantPage/components/ServiceTotals';
import {
    PricingServiceTypes,
    PricingTerms,
} from 'src/pages/AddMerchantPage/components/ServiceType';
import { ChargesValue } from 'src/pages/AddMerchantPage/components/TransactionCharges';
import OutletInformationComponent from 'src/pages/AddMerchantPage/steps/OutletInformation/components/OutletInformation';
import { getMinimumContractTerm } from 'src/utils/utils';

const AddServiceStep = () => {
    const { merchantId } = useParams();
    const [type, setType] = useState();
    const { LOCALITY_CONSTANT } = useTransactionData();

    const { additionalCharges } = useAdditionalCharges(type);
    const {
        addMerchant,
        merchantApplication,
        serviceApplication,
        findingDraftApplicationById,
        findingDraftApplication,
        addNewOutlet,
        isExistingOutlet,
    } = useAddMerchantContext();

    const { data: merchantData } = useQuery(GET_MERCHANT, {
        variables: { id: Number(merchantId) },
    });

    const formikInitialValues = {
        salesConditions: {
            merchantCategoryCode:
                merchantApplication?.serviceRequirements?.salesConditions?.merchantCategoryCode,
            monthlyTurnover:
                merchantApplication?.serviceRequirements?.salesConditions?.monthlyTurnover,
        },
        service: {
            serviceType: merchantApplication?.serviceRequirements?.service?.serviceType || '',
            contractTerm: '',
            selectedTerminalOffer: '',
            selectedOnlineOffer: '',
            selectedTransactionOffer: '',
            transactionPricingOfferId: '',
            promoOffer: '',
        },
        order: {
            primary: [],
            secondary: [],
        },
        isServiceBilled: merchantApplication?.serviceRequirements?.isServiceBilled || false,
        totals: { totalOneOff: null, totalMonthly: null, totalTerm: null },
        totalOrderPrice: {
            totalMonthly: [],
            totalOneOff: [],
        },
        transactionCharges: {
            personalCards: {} as ChargesValue,
            commercialCards: {} as ChargesValue,
            additionalCharges,
            locality: LOCALITY_CONSTANT,
            additionalTariffs: {} as ChargesValue,
            pciComplience: {} as ChargesValue,
        },
        isStepCompleted: false,
        outlets: [
            {
                name: '',
                siteContact: {
                    email: '',
                    firstName: '',
                    lastName: '',
                    position: '',
                    mobileNumber: '',
                    telephoneNumber: '',
                },
                tradingAddress: {
                    county: '',
                    country: 'Ireland',
                    eircode: '',
                    addressLine1: '',
                    addressLine2: '',
                    townOrCity: '',
                },
            },
        ],
        existingOutlet: 'Yes',
        ...serviceApplication?.serviceRequirements,
        ...serviceApplication?.merchantInformation,
    };

    const handleSubmit = async (serviceRequirements) => {
        const { outlets, ...rest } = serviceRequirements;
        const { outlets: _, ...merchantInfoRest } = merchantApplication?.merchantInformation || {};

        if (!isExistingOutlet && outlets.length > 0) {
            addNewOutlet(outlets[0]);
        }

        await addMerchant({
            applicationId: serviceApplication?.id ?? null,
            partnerId: merchantApplication.partnerId,
            businessInformation: merchantApplication?.businessInformation,
            partnerType: merchantApplication?.partnerType,
            serviceRequirements: { ...rest, isStepCompleted: true },
            isServiceApplication: true,
            merchantAccountNumber:
                merchantApplication?.merchantAccountNumber ||
                merchantData?.merchant?.merchantAccountNumber,
            merchantInformation: {
                ...merchantInfoRest,
                outlets:
                    outlets && outlets.length > 0
                        ? outlets
                        : merchantApplication?.merchantInformation?.outlets,
            },
        });
    };
    const merchantName = `${
        merchantApplication?.merchantInformation?.aboutBusiness.tradingName ?? ''
    }`;
    const partnerName = `${merchantApplication?.partner?.business?.legalName ?? ''}`;

    return (
        <Formik
            initialValues={formikInitialValues}
            enableReinitialize
            onSubmit={handleSubmit}
            validationSchema={addServiceRequirementsValidation(
                getPostalLabel(merchantApplication?.partner?.countryDetails?.alpha2Code)
            )}
            validateOnMount
        >
            {(formik) => {
                const shouldShowOrders =
                    formik.values.service.serviceType &&
                    formik.values.service.contractTerm &&
                    (formik.values.service.selectedOnlineOffer ||
                        formik.values.service.selectedTerminalOffer) &&
                    formik.values.service.serviceType !== PricingServiceTypes.ACQUIRING_ONLY;
                const chargeableContractTerm =
                    formik.values.service.contractTerm === PricingTerms.CUSTOM
                        ? formik.values.service.customTerm ?? 0
                        : contractTerms.find((term) => {
                              return term.value === formik.values.service.contractTerm;
                          })?.numberOfMonths || 0;
                const minimumContractTermInMonths =
                    formik.values.service.shouldApplyPromo === 'yes'
                        ? getMinimumContractTerm(formik.values)
                        : chargeableContractTerm;

                if (
                    formik.values.service.shouldApplyPromo === 'no' &&
                    formik.values.service.promoOffer
                ) {
                    formik.setFieldValue('service.promoOffer', undefined);
                }
                if (
                    formik.values.service.shouldApplyPromo === 'no' &&
                    formik.values.service.promoOffer
                ) {
                    formik.setFieldValue('service.promoOffer', undefined);
                }
                useEffect(() => {
                    setType(formik.values.service.serviceType);
                }, [formik.values.service.serviceType]);

                return (
                    <Form>
                        {findingDraftApplicationById || findingDraftApplication ? (
                            <Loader />
                        ) : (
                            <ProductOrderProvider>
                                <ServiceOrderProvider>
                                    <TransactionChargeProvider>
                                        <Box display="flex" flexDirection="column" gap={3}>
                                            <Box display="flex" gap={2} borderRadius={3}>
                                                <HeadingLabeledValueCard
                                                    title="Partner Name"
                                                    content={partnerName}
                                                />
                                                <HeadingLabeledValueCard
                                                    title="Merchant Name"
                                                    content={merchantName}
                                                />
                                            </Box>
                                            <AddServiceType
                                                contractTerms={contractTerms}
                                                hasExistingService={Boolean(
                                                    formikInitialValues.service.serviceType
                                                )}
                                            />

                                            {shouldShowOrders && (
                                                <>
                                                    <OutletInformationComponent
                                                        key={0}
                                                        index={0}
                                                        formik={formik}
                                                        remove={() => {}}
                                                        isServiceApplication
                                                    />

                                                    <ServiceOrders
                                                        serviceType={
                                                            formik.values.service
                                                                .serviceType as PricingServiceTypes
                                                        }
                                                        order={
                                                            formik.values.order as SelectedProducts
                                                        }
                                                        selectedOffer={
                                                            formik.values.service
                                                                .selectedOnlineOffer ||
                                                            formik.values.service
                                                                .selectedTerminalOffer
                                                        }
                                                    />
                                                    <ServiceTotals
                                                        chargeableContractTerm={
                                                            chargeableContractTerm
                                                        }
                                                        minimumContractTerm={
                                                            minimumContractTermInMonths
                                                        }
                                                    />
                                                    <ProductOrders
                                                        serviceType={
                                                            formik.values.service
                                                                .serviceType as PricingServiceTypes
                                                        }
                                                        contractTerm={chargeableContractTerm}
                                                    />
                                                </>
                                            )}

                                            <Box display="flex" justifyContent="flex-end">
                                                <Button
                                                    sx={{
                                                        alignSelf: 'end',
                                                        marginTop: '24px',
                                                        marginRight: '22px',
                                                    }}
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        window.history.back();
                                                    }}
                                                    variant="back-button"
                                                >
                                                    Back
                                                </Button>
                                                <Button
                                                    sx={{
                                                        alignSelf: 'end',
                                                        marginTop: '24px',
                                                    }}
                                                    type="submit"
                                                    variant="main"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        if (
                                                            formik.values.existingOutlet === 'Yes'
                                                        ) {
                                                            formik.setTouched({
                                                                ...formik.touched,
                                                                outlets: formik.values.outlets.map(
                                                                    (index) => {
                                                                        return {
                                                                            ...formik.touched
                                                                                .outlets?.[index],
                                                                            name: true,
                                                                        };
                                                                    }
                                                                ),
                                                                service: {
                                                                    serviceType: true,
                                                                    contractTerm: true,
                                                                    selectedTerminalOffer: true,
                                                                    selectedOnlineOffer: true,
                                                                    selectedTransactionOffer: true,
                                                                    transactionPricingOfferId: true,
                                                                    promoOffer: true,
                                                                },
                                                            });
                                                        } else {
                                                            // This is to prevent validation Errors on form fields of showing by default if an existing outlet of 'yes' was chosen
                                                            formik.setTouched({
                                                                ...formik.touched,
                                                                salesConditions: {
                                                                    merchantCategoryCode: true,
                                                                    monthlyTurnover: true,
                                                                },
                                                                service: {
                                                                    serviceType: true,
                                                                    contractTerm: true,
                                                                    selectedTerminalOffer: true,
                                                                    selectedOnlineOffer: true,
                                                                    selectedTransactionOffer: true,
                                                                    transactionPricingOfferId: true,
                                                                    promoOffer: true,
                                                                },
                                                                order: {
                                                                    primary:
                                                                        formik.values.order.primary.map(
                                                                            () => {
                                                                                return true;
                                                                            }
                                                                        ),
                                                                    secondary:
                                                                        formik.values.order.secondary.map(
                                                                            () => {
                                                                                return true;
                                                                            }
                                                                        ),
                                                                },
                                                                isServiceBilled: true,
                                                                totals: {
                                                                    totalOneOff: true,
                                                                    totalMonthly: true,
                                                                    totalTerm: true,
                                                                },
                                                                totalOrderPrice: {
                                                                    totalMonthly:
                                                                        formik.values.totalOrderPrice.totalMonthly.map(
                                                                            () => {
                                                                                return true;
                                                                            }
                                                                        ),
                                                                    totalOneOff:
                                                                        formik.values.totalOrderPrice.totalOneOff.map(
                                                                            () => {
                                                                                return true;
                                                                            }
                                                                        ),
                                                                },
                                                                transactionCharges: {
                                                                    personalCards: true,
                                                                    commercialCards: true,
                                                                    additionalCharges: true,
                                                                    locality: true,
                                                                    additionalTariffs: true,
                                                                    pciComplience: true,
                                                                },
                                                                isStepCompleted: true,
                                                                outlets: formik.values.outlets.map(
                                                                    () => {
                                                                        return {
                                                                            name: true,
                                                                            siteContact: {
                                                                                email: true,
                                                                                firstName: true,
                                                                                lastName: true,
                                                                                position: true,
                                                                                mobileNumber: true,
                                                                                telephoneNumber:
                                                                                    true,
                                                                            },
                                                                            tradingAddress: {
                                                                                county: true,
                                                                                country: true,
                                                                                eircode: true,
                                                                                addressLine1: true,
                                                                                addressLine2: true,
                                                                                townOrCity: true,
                                                                            },
                                                                        };
                                                                    }
                                                                ),
                                                                existingOutlet: true,
                                                            });
                                                        }

                                                        formik.validateForm().then((errors) => {
                                                            if (Object.keys(errors).length > 0) {
                                                                showErrorToast(
                                                                    'Please fill in required fields'
                                                                );
                                                            } else {
                                                                handleSubmit(formik.values);
                                                            }
                                                        });
                                                    }}
                                                >
                                                    Next
                                                </Button>
                                            </Box>
                                        </Box>
                                    </TransactionChargeProvider>
                                </ServiceOrderProvider>
                            </ProductOrderProvider>
                        )}
                    </Form>
                );
            }}
        </Formik>
    );
};

export default AddServiceStep;
